
import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { SessionService } from '../../session.service';
import { CallsService } from '../../calls.service';
import { AuthService } from '../../auth.service';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [CommonModule, RouterModule, ZXingScannerModule],
  template: `
    <div class="landing-container">
      <div class="card content-card">
        <div class="icon-circle">
          <span class="material-symbols-rounded">videocam</span>
        </div>
        
        <h2 class="brand-title">BabyMonitor<span class="live-badge">.live</span></h2>
        <h1>Baby Unit</h1>
        <p>Turn this device into a smart baby monitor camera.</p>
        
        <!-- Scanner / Button Area -->
        <div class="scanner-wrapper mt-4">
            @if (isProcessing) {
                <div class="scanner-placeholder processing-mode">
                    <span class="bad-state-icon material-symbols-rounded spin">sync</span>
                    <p>{{ status }}</p>
                </div>
            } @else if (!cameraEnabled) {
                <button class="btn btn-primary scanner-placeholder" (click)="enableCamera()">
                    <span class="material-symbols-rounded">qr_code_scanner</span>
                    Tap to Enable Camera
                </button>
            } @else {
                <div class="scanner-box">
                    <zxing-scanner 
                        [formats]="allowedFormats"
                        (scanSuccess)="onScanSuccess($event)"
                        class="scanner-feed">
                    </zxing-scanner>
                    <div class="overlay-text" *ngIf="status">
                        {{ status }}
                    </div>
                </div>
            }
        </div>

        <!-- Room List -->
        @if (pairedRooms.length > 0 && !cameraEnabled && !isProcessing) {
            <div class="room-list mt-4">
                <h3>My Rooms</h3>
                <div class="room-grid">
                    @for (room of pairedRooms; track room) {
                        <button class="room-btn" (click)="joinRoom(room)">
                            <span class="material-symbols-rounded">meeting_room</span>
                            <span class="room-name">{{ room }}</span>
                        </button>
                    }
                </div>
            </div>
        }

        <p class="hint">
          Scan the Room QR Code to join.
        </p>
      </div>
      
      <footer class="legal-footer">
        <a routerLink="/terms">Terms</a>
        <span class="separator">·</span>
        <a routerLink="/privacy">Privacy</a>
      </footer>
    </div>
  `,
  styles: [`
    :host {
      --primary: #FF6B6B;
      --secondary: #4ECDC4;
      --background: #FFFDF9;
      --surface: #FFFFFF;
      --text-main: #2D3436;
      --text-secondary: #636E72;
      --border: #F0F2F5;
      --radius: 20px;
      --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    .landing-container {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: var(--background);
      color: var(--text-main);
      padding: 1.25rem;
      font-family: 'Outfit', sans-serif;
    }

    .content-card {
      background: var(--surface);
      border-radius: var(--radius);
      padding: 2rem;
      box-shadow: var(--shadow-sm);
      border: 1px solid var(--border);
      max-width: 400px;
      width: 100%;
      text-align: center;
    }

    .icon-circle {
      width: 60px;
      height: 60px;
      background: #FFF0F0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1rem;
    }

    .icon-circle span {
      font-size: 32px;
      color: var(--primary);
    }

    .brand-title {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0 0 0.25rem;
      color: var(--text-main);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
    }

    .live-badge {
      font-size: 0.6rem;
      background: #FF0073;
      color: white;
      padding: 0.2rem 0.4rem;
      border-radius: 4px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      vertical-align: middle;
      transform: translateY(-2px);
      box-shadow: 0 2px 6px rgba(220, 39, 67, 0.3);
    }

    h1 {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 0 0 0.5rem;
      color: var(--text-secondary);
    }

    p {
      color: var(--text-secondary);
      margin: 0;
      line-height: 1.5;
      font-size: 0.95rem;
    }

    .mt-4 { margin-top: 2rem; }

    /* Scanner Styles */
    .scanner-wrapper {
        width: 100%;
        max-width: 320px;
        margin-left: auto;
        margin-right: auto;
        aspect-ratio: 1 / 1;
        position: relative;
        overflow: hidden;
        border-radius: var(--radius);
        background: #000;
    }

    .scanner-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        background: var(--primary);
        color: white;
        border: none;
        cursor: pointer;
        padding: 0;
        font-family: inherit;
        font-weight: 600;
        font-size: 1.1rem;
        transition: opacity 0.2s;
    }
    
    .scanner-placeholder span {
        font-size: 48px;
    }

    .scanner-placeholder:active {
        opacity: 0.9;
    }

    .processing-mode {
        background: #2d3436; 
        color: white;
        cursor: default;
    }
    
    .spin {
        animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(-360deg); }
    }

    .scanner-box {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        background: black;
    }

    ::ng-deep zxing-scanner {
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }

    ::ng-deep zxing-scanner video {
        object-fit: cover !important;
        width: 100% !important;
        height: 100% !important;
        min-height: 100%;
        min-width: 100%;
        border-radius: var(--radius);
    }

    .overlay-text {
        position: absolute;
        bottom: 1rem;
        left: 0; 
        right: 0;
        text-align: center;
        color: white;
        background: rgba(0,0,0,0.5);
        padding: 0.5rem;
        font-size: 0.9rem;
        pointer-events: none;
    }
    
    .hint {
      margin-top: 1.5rem;
      font-size: 0.9rem;
      opacity: 0.8;
    }

    .legal-footer {
      margin-top: 2rem;
      font-size: 0.85rem;
      color: var(--text-secondary);
      opacity: 0.8;
    }
    
    .legal-footer a {
      color: inherit;
      text-decoration: none;
    }
    
    .legal-footer .separator {
      margin: 0 0.5rem;
    }

    /* Room List Styles */
    .room-list {
        width: 100%;
        max-width: 320px;
        margin: 2rem auto 0;
    }

    .room-list h3 {
        font-size: 1rem;
        color: var(--text-secondary);
        margin-bottom: 0.75rem;
        font-weight: 600;
        text-align: left;
    }

    .room-grid {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .room-btn {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        background: #F8F9FA;
        border: 1px solid var(--border);
        border-radius: 12px;
        width: 100%;
        text-align: left;
        cursor: pointer;
        transition: all 0.2s;
        font-family: inherit;
        color: var(--text-main);
    }

    .room-btn:active {
        background: #E9ECEF;
        transform: scale(0.98);
    }

    .room-btn span.material-symbols-rounded {
        color: var(--primary);
        font-size: 1.5rem;
    }

    .room-name {
        font-weight: 600;
        font-size: 1rem;
    }
  `]
})
export class LandingComponent implements OnInit {
  cameraEnabled = false;
  isProcessing = false;
  allowedFormats = [11]; // QR_CODE
  status = '';

  pairedRooms: string[] = [];

  constructor(
    private sessionService: SessionService,
    private callsService: CallsService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private auth: AuthService
  ) { }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async ngOnInit() {
    // 1. Check for Auto-Rejoin (Last Room ID)
    const lastRoomId = this.sessionService.getLastRoomId();

    // Wait for Auth to be ready
    const sub = this.auth.user$.subscribe(async user => {
      if (user) {
        // Fetch paired rooms
        this.pairedRooms = await this.sessionService.getPairedRooms();
        this.cdr.detectChanges();

        if (lastRoomId) {
          this.status = 'Verifying session...';
          this.cdr.detectChanges();

          sub.unsubscribe(); // Run once if we are checking last room

          const isValid = await this.sessionService.verifyRoomPersistence(lastRoomId);
          if (isValid) {
            this.status = 'Rejoining...';
            this.cdr.detectChanges();
            this.router.navigate(['/monitor', lastRoomId]);
          } else {
            console.log('[Landing:ngOnInit] Previous session invalid or expired');
            this.sessionService.clearRoom();
            this.status = '';
            this.cdr.detectChanges();
          }
        }
      }
    });

    // 2. Fragment Handler
    this.route.fragment.subscribe(async (fragment) => {
      if (fragment) {
        this.status = 'Joining...';
        this.joinRoom(fragment);
      }
    });

    // 3. Query Param Handler (if invited link uses ?roomId=...)
    this.route.queryParams.subscribe(params => {
      if (params['roomId']) {
        this.joinRoom(params['roomId']);
      }
    });
  }

  async enableCamera() {
    console.log('[Landing:enableCamera] Requesting permissions...');
    try {
      // Request both audio and video permissions upfront
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      console.log('[Landing:enableCamera] Permissions granted. Stopping check-stream...');

      // Stop the stream immediately
      stream.getTracks().forEach(track => track.stop());

      console.log('[Landing:enableCamera] Stream stopped. Waiting for resource release...');
      await this.delay(1000); // Wait 1s for hardware release

      console.log('[Landing:enableCamera] Enabling Scanner...');
      this.cameraEnabled = true;
      this.cdr.detectChanges();
    } catch (permErr) {
      console.error('[Landing] Permission denied', permErr);
      alert('Camera and Microphone permissions are required to use this app.');
    }
  }

  async onScanSuccess(result: string) {
    // 1. Stop Scanner Immediately to release camera resource
    this.cameraEnabled = false;
    this.isProcessing = true;
    this.cdr.detectChanges();

    console.log('[Landing:onScanSuccess] Scanned code:', result);
    console.log('[Landing:onScanSuccess] Scanner disabled. Waiting for resource release...');

    // Give time for zxing to release camera
    await this.delay(1000);

    let roomId: string | null = null;
    let inviteId: string | null = null;

    try {
      // Try JSON first
      const data = JSON.parse(result);
      if (data.roomId) roomId = data.roomId;
      if (data.inviteId) inviteId = data.inviteId;
    } catch {
      // Try URL
      if (result.includes('/monitor/')) {
        roomId = result.split('/monitor/')[1].split('?')[0];
      } else if (result.includes('roomId=')) {
        const params = new URLSearchParams(result.split('?')[1]);
        roomId = params.get('roomId');
      } else {
        // Assume raw ID if it looks like one
        roomId = result;
      }
    }

    if (inviteId) {
      this.status = 'Handshaking...';
      try {
        console.log('[Landing:onScanSuccess] Joining via invite:', inviteId);
        roomId = await this.callsService.joinViaInvite(inviteId);
      } catch (inviteErr) {
        console.error('[Landing:onScanSuccess] Invite failed', inviteErr);
        this.status = 'Invite Failed';
        this.cdr.detectChanges();
        return;
      }
    }

    if (roomId) {
      this.status = 'Joining...';
      this.joinRoom(roomId);
    } else {
      this.status = 'Invalid QR Code';
      this.cdr.detectChanges();
      setTimeout(() => {
        this.status = '';
        this.cdr.detectChanges();
      }, 3000);
    }
  }

  joinRoom(roomId: string) {
    if (roomId) {
      this.router.navigate(['/monitor', roomId]);
    }
  }
}
